/**
 * Senders + Texts mode rows for multi-country bulk edit AI panel (single wrapping row).
 */

import { MessageSquare, User } from 'lucide-react';

import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';
import { AiGenerateDropdown } from '@/features/campaign-texts/components/AiGenerateDropdown';
import { AiGenerateSendersDropdown } from '@/features/campaign-texts/components/AiGenerateSendersDropdown';
import type { DropLabel } from '@/features/campaign-texts/components/PerDropGroupSelector';

import type { CountryAiMode, useMultiCountryCreative } from '../hooks/useMultiCountryCreative';

type Creative = ReturnType<typeof useMultiCountryCreative>;

const MODE_OPTS: Array<{ value: CountryAiMode; label: string }> = [
  { value: 'same_all', label: 'Same for all' },
  { value: 'per_country', label: 'Per country' },
  { value: 'per_drop', label: 'Per drop' },
];

interface Props {
  creative: Creative;
  dropLabels?: DropLabel[];
  showAi: boolean;
  showAiSenders: boolean;
  onTextGenError: (err: unknown) => void;
  onSenderGenError: (err: unknown) => void;
}

export function MultiCountryAiGenerationRows({
  creative,
  dropLabels,
  showAi,
  showAiSenders,
  onTextGenError,
  onSenderGenError,
}: Props) {
  if (!showAiSenders && !showAi) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
      {showAiSenders && (
        <GenerateRow
          label="Senders" icon={<User className="h-3.5 w-3.5" />}
          mode={creative.senderMode} onModeChange={creative.setSenderMode}
          renderButton={
            <AiGenerateSendersDropdown
              countryKey={creative.countryKeys[0] ?? ''}
              dropCount={creative.totalDropCount}
              dropLabels={dropLabels}
              delegateGeneration
              creativeScope={creative.senderMode}
              delegatedBusy={creative.isGenerating}
              onDelegatedGenerate={({ groupId, amount, groupIdsPerDrop }) => {
                void creative
                  .generateSendersForAllCountries(groupId, amount, groupIdsPerDrop)
                  .catch(onSenderGenError);
              }}
            />
          }
        />
      )}
      {showAiSenders && showAi && (
        <div className="hidden h-6 w-px shrink-0 bg-border/60 sm:block" aria-hidden />
      )}
      {showAi && (
        <GenerateRow
          label="Texts" icon={<MessageSquare className="h-3.5 w-3.5" />}
          mode={creative.textMode} onModeChange={creative.setTextMode}
          renderButton={
            <AiGenerateDropdown
              countryKey={creative.countryKeys[0] ?? ''}
              dropCount={creative.totalDropCount}
              dropLabels={dropLabels}
              delegateGeneration
              creativeScope={creative.textMode}
              delegatedBusy={creative.isGenerating}
              onDelegatedGenerate={({ groupId, amount, translate, groupIdsPerDrop }) => {
                void creative
                  .generateTextsForAllCountries(groupId, amount, groupIdsPerDrop, translate)
                  .catch(onTextGenError);
              }}
            />
          }
        />
      )}
    </div>
  );
}

function GenerateRow({ label, icon, mode, onModeChange, renderButton }: {
  label: string;
  icon: React.ReactNode;
  mode: CountryAiMode;
  onModeChange: (m: CountryAiMode) => void;
  renderButton: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <span className="flex items-center gap-1.5 text-xs font-semibold">
        {icon} {label}
      </span>
      <div className="flex gap-0.5 rounded-md border border-border bg-muted/30 p-0.5">
        {MODE_OPTS.map((opt) => (
          <Button
            key={opt.value} type="button" size="sm"
            variant={mode === opt.value ? 'default' : 'ghost'}
            className={cn('h-6 px-2 text-[11px]', mode !== opt.value && 'text-muted-foreground')}
            onClick={() => onModeChange(opt.value)}
          >
            {opt.label}
          </Button>
        ))}
      </div>
      <div className="shrink-0">{renderButton}</div>
    </div>
  );
}
